import { FC, ReactNode } from "react";
import { Box, makeStyles, Typography } from "@material-ui/core";

import { breakpoints } from "containers/Responsive";
import { DynamicRestQueryOptions } from "lib/dynamicRest";
import { useInventoryListingsPaginated } from "services/inventory";
import { InventoryListing } from "services/inventory/types";
import theme from "theme";
import {
  desktopHeaderHeight,
  headerHideTransition,
  mobileHeaderHeight,
} from "theme/constants";

import LoadingTable from "./components/LoadingTable";
import TableHeader from "./components/TableHeader";
import TableRow from "./components/TableRow";
import TableSection from "./components/TableSection";
import { TTableColumn } from "./types";

const useStyles = makeStyles(theme => ({
  container: {
    width: "100%",
    border: "1px solid #e1e1e3",
    [`@media (max-width: ${breakpoints.desktop - 1}px)`]: {
      fontSize: "80%",
    },
  },
  tableWrapper: {
    width: "100%",
    [`@media (max-width: ${breakpoints.desktop - 1}px)`]: {
      fontSize: "95%",
    },
    "& table": {
      width: "100%",
      position: "relative",
    },
    "& thead": {
      userSelect: "none",
      "& th": {
        top: 0,
        position: "sticky",
        backgroundColor: theme.custom.palette.softWhite.main,
        padding: "0.5rem 1rem",
        fontWeight: theme.typography.fontWeightMedium,
        textAlign: "left",
        cursor: "pointer",
        whiteSpace: "nowrap",
        minWidth: 25,
        height: 50,
        transition: headerHideTransition,
        zIndex: 100,
        "&:first-of-type, &:last-of-type": {
          zIndex: 99,
        },
        "&:first-of-type": {
          boxShadow: "-1px 0px 0px 0px rgba(225, 225, 227, 1)",
        },
        "&:last-of-type": {
          boxShadow: "1px 0px 0px 0px rgba(225, 225, 227, 1)",
        },
        [`@media (min-width: ${breakpoints.desktop}px)`]: {
          fontSize: "80%",
          "nav.nav.-open ~ #react-root &": {
            top: `${desktopHeaderHeight}px`,
          },
        },
        [`@media (max-width: ${breakpoints.desktop - 1}px)`]: {
          padding: "0.5rem 0.3rem",
          "nav.navigation.-open ~ #react-root &": {
            top: `${mobileHeaderHeight}px`,
          },
        },
        "& .fa": {
          paddingLeft: theme.spacing(0.25),
          color: "grey",
          cursor: "pointer",
        },
        "& > span.icon": {
          marginRight: theme.spacing(0.625),
          "& > img": {
            height: "calc(100% - 3px)",
            marginBottom: theme.spacing(0.25),
          },
        },
        "& > span.label": {
          display: "inline-flex",
          flexDirection: "column",
          verticalAlign: "middle",
          "& > span:nth-of-type(1)": {
            width: "fit-content",
          },
          "& > span:nth-of-type(2)": {
            fontSize: "90%",
            color: theme.palette.text.secondary,
          },
        },
      },
      "& th:after": {
        content: '""',
        position: "absolute",
        left: 0,
        bottom: 0,
        width: "100%",
        borderBottom: "2px solid #eeeeee",
      },
    },
    "& tbody tr": {
      backgroundColor: "#ffffff",
      transition: "background-color 180ms cubic-bezier(0.2, 0.3, 0.25, 0.9)",
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
      "&:not(:last-child)": {
        borderBottom: "1px solid #e1e1e3",
      },
    },
    "& tbody td": {
      padding: "0.5rem 0.3rem",
      paddingLeft: "1rem",
      minWidth: 25,
      fontSize: 14,
      [`@media (max-width: ${breakpoints.desktop - 1}px)`]: {
        paddingLeft: "0.3rem",
      },
    },
  },
  featuredLastRow: {
    border: `1px solid ${theme.custom.palette.purple.main} !important`,
    "& td": {
      padding: "0 !important",
      margin: "0 !important",
    },
  },
}));

interface ListingTableProps {
  columns: TTableColumn<InventoryListing>[];
  listingsQueryOptions: DynamicRestQueryOptions;
  activeSortName?: string;
  prevUrl?: string;
}

interface TableRowsProps
  extends Omit<ListingTableProps, "listingsQueryOptions" | "page"> {
  listings: InventoryListing[];
  isColorBorder?: true;
}

const TableRows: FC<TableRowsProps> = ({
  columns,
  listings,
  prevUrl,
  isColorBorder,
}) => {
  return (
    <>
      {listings.map((listing: InventoryListing) => {
        return (
          <TableRow
            key={`${listing.listing_id}-${listing.kind}`}
            columns={columns}
            listing={listing}
            prevUrl={prevUrl}
            isColorBorder={isColorBorder}
          />
        );
      })}
    </>
  );
};

const FullWidthTableSection: FC<{
  columns: TTableColumn<InventoryListing>[];
  children: ReactNode;
}> = ({ columns, children }) => (
  <tr>
    {/* `+ 2` to account for `hasLeftCol hasRightCol` see TableHeader */}
    <td colSpan={columns.length + 2} style={{ padding: "unset" }}>
      {children}
    </td>
  </tr>
);

const NoListingsMessage = () => (
  <Box display="flex" justifyContent="center" padding={`${theme.spacing(4)}px`}>
    <Typography>No Listings match your current filters</Typography>
  </Box>
);

const FeaturedLastRow: FC<{
  columns: TTableColumn<InventoryListing>[];
}> = ({ columns }) => {
  const classes = useStyles();
  return (
    <tr className={classes.featuredLastRow}>
      {/* Safari hack to fix the border bottom the two td outside the loop is to include the arrow */}
      {columns.map(column => (
        <td colSpan={0} key={column.primaryKey}></td>
      ))}
      <td colSpan={0}></td>
      <td colSpan={0}></td>
    </tr>
  );
};

const ListingTable: FC<ListingTableProps> = ({
  columns,
  listingsQueryOptions,
  activeSortName,
  ...restProps
}) => {
  const classes = useStyles();
  const listingsQueries = useInventoryListingsPaginated(listingsQueryOptions);
  const listings = listingsQueries.data?.pages?.[0].results ?? [];

  const auctionListings = listings.filter(
    listing => listing.kind === "auction",
  );
  const featuredListings = listings.filter(
    listing => !!listing.featured_position,
  );

  const showColorSections =
    activeSortName === "Newest" &&
    (auctionListings.length > 0 || featuredListings.length > 0);

  const otherListings = showColorSections
    ? listings.filter(
        listing => !listing.featured_position && listing.kind !== "auction",
      )
    : listings;

  return (
    <Box className={classes.container}>
      <Box className={classes.tableWrapper}>
        <table id="exchange-listings-table" aria-label="exchange listings">
          <TableHeader columns={columns} />
          <tbody>
            {showColorSections && auctionListings.length > 0 && (
              <>
                <TableSection isColorBorder title="Auctions" />
                <TableRows
                  {...restProps}
                  columns={columns}
                  listings={auctionListings}
                  isColorBorder={showColorSections}
                />
                <FeaturedLastRow columns={columns} />
              </>
            )}

            {showColorSections && featuredListings.length > 0 && (
              <>
                <TableSection isColorBorder title="Featured Listings" />
                <TableRows
                  {...restProps}
                  columns={columns}
                  listings={featuredListings}
                  isColorBorder={showColorSections}
                />
                <FeaturedLastRow columns={columns} />
              </>
            )}

            {listingsQueries.isLoading && (
              <FullWidthTableSection columns={columns}>
                <LoadingTable />
              </FullWidthTableSection>
            )}
            {!listingsQueries.isLoading && !listings?.length && (
              <FullWidthTableSection columns={columns}>
                <NoListingsMessage />
              </FullWidthTableSection>
            )}
            <TableRows
              {...restProps}
              columns={columns}
              listings={otherListings}
            />
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export default ListingTable;
